import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useBorrower1003Store } from '../../../borrower1003Store';
import { borrower1003QueryKeys, use1003BorrowerData } from '../../../hooks';
import type { BorrowerData } from '../../../types';

export function useBorrowerDataFormData(borrowerId: number) {
  return use1003BorrowerData({
    select: useCallback(
      (s) => {
        const borrower = s.borrowers.find(
          (b) => b.customerRecordId === borrowerId,
        );

        const shouldHydratePowerOfAttorneySigner =
          Boolean(borrower?.powerOfAttorney) ||
          Boolean(borrower?.signer) ||
          Boolean(borrower?.mannerPowerOfAttorneyHeld);

        return {
          formData: {
            ...borrower,
            isNameOrSsnChanged: false,
            isJoinedBorrowerChanged: false,
            powerOfAttorneySigners: shouldHydratePowerOfAttorneySigner
              ? [
                  {
                    signer: borrower?.signer,
                    mannerPowerOfAttorneyHeld:
                      borrower?.mannerPowerOfAttorneyHeld,
                  },
                ]
              : [],
          },
          isVA: s.isVA ?? false,
          dropdownOptions: {
            ...s.dropdownOptions,
            joinedBorrowers: borrower?.joinedBorrowerDropdownOptions ?? [],
          },
        };
      },
      [borrowerId],
    ),
  });
}

export function useBorrowerDataMutation(borrowerId: number) {
  const showSnackbar = useSnackbar();
  const { obfuscatedLoanIdentifier } = useUrl();
  const queryClient = useQueryClient();
  const { data } = useBorrowerDataFormData(borrowerId);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({
      powerOfAttorneySigners,
      ...values
    }: BorrowerData['formData']) => {
      const numberOfFamilyMembers =
        values.numberOfFamilyMembers == null ||
        String(values.numberOfFamilyMembers).trim() === ''
          ? null
          : Number(values.numberOfFamilyMembers);

      await typedApiClient.v1.borrower1003ValidationBorrowerSummary({
        obfuscatedLoanIdentifier,
        borrower: {
          ...values,
          numberOfFamilyMembers,
          borrowerDeclarations: undefined,
          residenceHistoryDetails: undefined,
          powerOfAttorney: !!powerOfAttorneySigners?.[0],
          signer: powerOfAttorneySigners?.[0]?.signer ?? null,
          mannerPowerOfAttorneyHeld:
            powerOfAttorneySigners?.[0]?.mannerPowerOfAttorneyHeld ?? null,
          joinedBorrower:
            (values.joinedBorrower?.borrowerRecordId as number | string) === ''
              ? {
                  borrowerName: 'None',
                  borrowerRecordId: 0,
                  borrowerLenderDatabaseId: 1,
                }
              : values.joinedBorrower,
        },
        shouldTriggerUwaiEntityMatching:
          data?.formData.firstName !== values.firstName ||
          data?.formData.middleName !== values.middleName ||
          data?.formData.lastName !== values.lastName,
      });
    },
    onSuccess: () => {
      useBorrower1003Store.setState({ selectedBorrowerId: null });

      queryClient.invalidateQueries({
        queryKey: borrower1003QueryKeys.borrowerData(obfuscatedLoanIdentifier),
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        message: 'Borrower data updated successfully.',
        severity: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Failed to update borrower data.',
        severity: 'error',
      });
    },
  });
}
